import { ReactNode, useCallback, useEffect, useId, useState } from 'react';
import { api } from '../../lib/api';
import { Button, Icon } from '../ui';

/** Loads `path` and lets the caller reload it after a change. */
export function useLoad<T>(path: string): [T | null, () => void, string | null] {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    api<T>('GET', path)
      .then((d) => {
        if (live) {
          setData(d);
          setError(null);
        }
      })
      .catch((err) => live && setError((err as Error).message));
    return () => {
      live = false;
    };
  }, [path, tick]);
  return [data, useCallback(() => setTick((t) => t + 1), []), error];
}

export function SectionHeader({ title, lead, action }: { title: string; lead: string; action?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div className="max-w-[640px]">
        <h1 className="text-lg font-semibold">{title}</h1>
        <p className="mt-1 text-sm text-ink-2">{lead}</p>
      </div>
      {action}
    </div>
  );
}

export function AddButton({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <Button tone="primary" className="px-3.5 py-2" onClick={onClick}>
      <Icon name="plus" size={15} /> {children}
    </Button>
  );
}

/** A plain table: header row, then rows; rows are buttons when clickable. */
export function Table({ head, children, empty }: { head: string[]; children: ReactNode; empty?: string | false }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-line bg-hull/60">
      <table className="w-full min-w-[640px] text-left text-sm">
        <thead className="text-ink-3">
          <tr className="border-b border-line">
            {head.map((h, i) => (
              <th key={h || i} className="px-4 py-2.5 font-normal">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
      {empty ? <p className="px-4 py-8 text-center text-sm text-ink-2">{empty}</p> : null}
    </div>
  );
}

export function Row({ children, onClick, muted = false }: { children: ReactNode; onClick?: () => void; muted?: boolean }) {
  return (
    <tr
      onClick={onClick}
      onKeyDown={(e) => onClick && (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onClick())}
      tabIndex={onClick ? 0 : undefined}
      className={`border-b border-line/60 last:border-0 ${onClick ? 'cursor-pointer hover:bg-veil/[0.04] focus-visible:bg-veil/[0.06]' : ''} ${muted ? 'opacity-55' : ''}`}
    >
      {children}
    </tr>
  );
}

export function Cell({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
}

export function Pill({ children, tone = 'line' }: { children: ReactNode; tone?: 'line' | 'good' | 'bad' | 'warn' }) {
  const c = {
    line: 'border-line text-ink-2',
    good: 'border-available/40 text-available',
    bad: 'border-break/40 text-break',
    warn: 'border-wrapup/40 text-wrapup',
  }[tone];
  return <span className={`inline-flex items-center rounded-md border px-1.5 py-px text-xs ${c}`}>{children}</span>;
}

/** Slide-over panel for create and edit forms. */
export function Drawer({
  title,
  open,
  onClose,
  children,
  footer,
}: {
  title: string;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-abyss/60 backdrop-blur-[2px]" onMouseDown={onClose}>
      <aside
        role="dialog"
        aria-label={title}
        className="flex h-full w-full max-w-[460px] flex-col border-l border-line-strong bg-hull shadow-2xl shadow-black/40"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-line px-5">
          <h2 className="font-semibold">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="grid h-8 w-8 place-items-center rounded-lg text-ink-2 hover:bg-veil/[0.06] hover:text-ink">
            <Icon name="close" size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-auto px-5 py-5">{children}</div>
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-t border-line px-5 py-3">{footer}</div>
      </aside>
    </div>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: (id: string) => ReactNode }) {
  const id = useId();
  return (
    <div className="mb-4">
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium">
        {label}
      </label>
      {children(id)}
      {hint ? <p className="mt-1 text-xs text-ink-3">{hint}</p> : null}
    </div>
  );
}

export const inputClass =
  'w-full rounded-lg border border-line bg-abyss/50 px-3 py-2 text-sm outline-none transition focus:border-ion disabled:opacity-50';

export function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label className="mb-4 flex cursor-pointer items-center justify-between gap-3 text-sm">
      <span>{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative h-6 w-11 shrink-0 rounded-full transition ${checked ? 'bg-ion' : 'bg-line-strong'}`}
      >
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${checked ? 'left-[22px]' : 'left-0.5'}`} />
      </button>
    </label>
  );
}

/** Two-step delete: first click asks, second click does it. No browser pop-ups. */
export function ConfirmButton({ children, confirmText, onConfirm }: { children: ReactNode; confirmText: string; onConfirm: () => void }) {
  const [asking, setAsking] = useState(false);
  useEffect(() => {
    if (!asking) return;
    const t = window.setTimeout(() => setAsking(false), 4000);
    return () => window.clearTimeout(t);
  }, [asking]);
  return asking ? (
    <Button tone="danger" className="px-3 py-2" onClick={onConfirm}>
      {confirmText}
    </Button>
  ) : (
    <Button tone="ghost" className="px-3 py-2 text-break" onClick={() => setAsking(true)}>
      {children}
    </Button>
  );
}

/** A random, readable temporary password. */
export function tempPassword(): string {
  const words = ['Nova', 'Coral', 'River', 'Cedar', 'Amber', 'Delta', 'Harbor', 'Summit'];
  const pick = () => words[crypto.getRandomValues(new Uint32Array(1))[0] % words.length];
  const n = 1000 + (crypto.getRandomValues(new Uint32Array(1))[0] % 9000);
  return `${pick()}-${pick()}-${n}`;
}

export function when(iso: string | null): string {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

const RETENTION_DAYS = [30, 90, 180, 365, 730, 1825];

/** How long recordings are kept. `none` is the label for "no setting": the org default, or forever. */
export function RetentionField({ value, none, hint, onChange }: { value: number | null; none: string; hint: string; onChange: (days: number | null) => void }) {
  const options = value && !RETENTION_DAYS.includes(value) ? [...RETENTION_DAYS, value].sort((a, b) => a - b) : RETENTION_DAYS;
  return (
    <Field label="Keep recordings for" hint={hint}>
      {(id) => (
        <select id={id} className={inputClass} value={value ?? ''} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}>
          <option value="">{none}</option>
          {options.map((d) => (
            <option key={d} value={d}>
              {d % 365 === 0 ? `${d / 365} year${d === 365 ? '' : 's'}` : `${d} days`}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}
